import { useTranslation } from "react-i18next";

import type { CaseListItem } from "@/domain/case/case";
import { isInteractiveTarget } from "@/lib/dom/is-interactive-target";
import { formatDate } from "@/lib/format/datetime";

import { CaseStatusBadge } from "./case-status-badge";

export type MyApplicationTableProps = {
  readonly items: readonly CaseListItem[];
  /** 行を押したときの遷移。詳細ドロワーは後続で接続する。 */
  readonly onSelect?: (_caseId: number) => void;
  /**
   * 起票者の列を出すか。「関与した案件」では自分の申請と他人の申請が混ざるので
   * 誰の申請かを示す。自分の起票だけを見ているときは全行が自分なので出さない。
   */
  readonly showInitiator?: boolean;
  /** 今ログインしている利用者。自分の行は名前ではなく「自分」と出す。 */
  readonly currentUsername?: string | null;
};

/**
 * マイ案件の一覧表。列は issue #29 の確定仕様（案件番号 / タイトル / ステータス /
 * 現在ステップ / 起票日 / 完了日）＋「関与した案件」のときだけ起票者。
 *
 * 取得・フィルタは呼び出し側の責務にして、この表は描画だけを担う（テストしやすさ優先）。
 */
export function MyApplicationTable({
  items,
  onSelect,
  showInitiator = false,
  currentUsername = null,
}: MyApplicationTableProps) {
  const { t } = useTranslation(["domain", "common"]);

  if (items.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {t("common:state.empty")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/50 text-muted-foreground text-left text-xs">
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:caseNumber")}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:case")}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:applicationType")}
            </th>
            {showInitiator ? (
              <th scope="col" className="px-3 py-2 font-medium">
                {t("domain:applicant")}
              </th>
            ) : null}
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:status")}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:currentStep")}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:submittedAt")}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t("domain:completedAt")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.caseId}
              // 行全体で詳細を開く。キーボード操作は行内の案件番号ボタンが担う
              // （tr は focus できないため、行に tabIndex を足すより素直）
              onClick={(event) => {
                if (onSelect === undefined) return;
                if (isInteractiveTarget(event.target)) return;
                onSelect(item.caseId);
              }}
              className={`hover:bg-muted/40 border-border border-b last:border-0 ${
                onSelect ? "cursor-pointer" : ""
              }`}
            >
              <td className="px-3 py-2 tabular-nums">
                {onSelect ? (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => onSelect(item.caseId)}
                  >
                    {item.caseNumber}
                  </button>
                ) : (
                  <span className="text-muted-foreground">
                    {item.caseNumber}
                  </span>
                )}
              </td>
              {/* 件名は起票フォームの値。未設定でもモデル名で埋めない
                  （検索対象と表示を一致させる） */}
              <td className="px-3 py-2">{item.title ?? "—"}</td>
              <td className="text-muted-foreground px-3 py-2">
                {item.processModelDisplayName}
              </td>
              {showInitiator ? (
                <td className="px-3 py-2">
                  {item.initiator === null
                    ? "—"
                    : item.initiator === currentUsername
                      ? t("common:myApplications.selfInitiator")
                      : item.initiator}
                </td>
              ) : null}
              <td className="px-3 py-2">
                <CaseStatusBadge status={item.status} />
              </td>
              <td className="px-3 py-2">{item.currentStep?.label ?? "—"}</td>
              <td className="text-muted-foreground px-3 py-2 tabular-nums">
                {formatDate(item.submittedAt)}
              </td>
              <td className="text-muted-foreground px-3 py-2 tabular-nums">
                {formatDate(item.completedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
